import { buildLocalizedShopText } from '../features/shopLocalization';
import type { MaybeLocalizedText } from '../i18n/locale';

export { buildLocalizedShopText } from '../features/shopLocalization';

export interface ProductButtonOption {
  value: string;
  label: MaybeLocalizedText;
  description?: MaybeLocalizedText;
  disabled?: boolean;
  stockCount?: number | null;
}

export interface ProductColorOption {
  label: MaybeLocalizedText;
  hex: string;
  price?: number;
  code?: string;
  isPlaceholder?: boolean;
  swatchImageUrl?: string;
  inventoryIds?: string[];
}

export interface ProductButtonOptionGroup {
  key: string;
  label: MaybeLocalizedText;
  type: 'button';
  options: ProductButtonOption[];
  pill?: boolean;
  required?: boolean;
  defaultValue?: string;
}

export interface ProductColorOptionGroup {
  key: string;
  label: MaybeLocalizedText;
  type: 'color';
  presetColors?: ProductColorOption[];
  required?: boolean;
  defaultValue?: ProductColorOption;
}

export type ProductOptionGroup = ProductButtonOptionGroup | ProductColorOptionGroup;

const STANDARD_SIZE_OPTIONS: ProductButtonOption[] = [
  { value: 'Custom', label: buildLocalizedShopText('Custom Size', '定制尺码') },
  { value: 'S', label: 'S' },
  { value: 'M', label: 'M' },
  { value: 'L', label: 'L' },
  { value: 'XL', label: 'XL' },
  { value: 'XXL', label: 'XXL' },
];

const STANDARD_FIT_OPTIONS: ProductButtonOption[] = [
  { value: 'Slim', label: buildLocalizedShopText('Slim', '修身') },
  { value: 'Standard', label: buildLocalizedShopText('Standard', '常规') },
  { value: 'Baggy', label: buildLocalizedShopText('Baggy', '宽松') },
];

export const STANDARD_FABRIC_OPTIONS: ProductButtonOption[] = [
  {
    value: 'Stretchy',
    label: 'Stretchy',
    description: buildLocalizedShopText(
      'Our stretchy blend is mostly cotton with a touch of spandex for easy movement and everyday comfort.',
      '我们的弹力混纺以棉为主，并加入少量氨纶，方便活动，穿着舒适，适合日常。'
    ),
  },
  {
    value: 'Durable',
    label: 'Durable',
    description: buildLocalizedShopText(
      'Our durable blend combines cotton and polyester for a fabric that holds its shape and stands up to regular wear.',
      '我们的耐穿混纺结合棉和聚酯纤维，面料更能保持版型，也经得起日常穿着。'
    ),
  },
  {
    value: 'Soft',
    label: 'Soft',
    description: buildLocalizedShopText(
      'Our soft blend uses rayon with other fibers for a smooth, lightweight, and more luxurious hand-feel.',
      '我们的柔软混纺采用人造丝搭配其他纤维，手感顺滑轻盈，触感更显高级。'
    ),
  },
  {
    value: 'Cotton',
    label: 'Cotton',
    description: buildLocalizedShopText(
      'Our cotton option is made from 100% cotton for a natural feel, breathable comfort, and a classic finish.',
      '我们的纯棉选项采用 100% 棉制成，带来自然手感、透气舒适度和经典质感。'
    ),
  },
  {
    value: 'Athletic',
    label: 'Athletic',
    description: buildLocalizedShopText(
      'Our athletic fabric uses 100% polyester for a lighter, performance-focused feel with easy care and durability.',
      '我们的运动面料采用 100% 聚酯纤维，手感更轻盈，偏重运动表现，同时兼顾易打理和耐穿性。'
    ),
  },
];

export const STANDARD_CUSTOMIZATION_GROUPS: ProductOptionGroup[] = [
  {
    key: 'size',
    label: buildLocalizedShopText('Size', '尺码'),
    type: 'button',
    options: STANDARD_SIZE_OPTIONS,
    pill: true,
    required: true,
    defaultValue: 'S',
  },
  {
    key: 'fit',
    label: buildLocalizedShopText('Fit', '版型'),
    type: 'button',
    options: STANDARD_FIT_OPTIONS,
    required: true,
  },
  {
    key: 'fabric',
    label: buildLocalizedShopText('Fabric Blend', '面料混纺'),
    type: 'button',
    options: STANDARD_FABRIC_OPTIONS,
    required: true,
  },
];

export const ACCESSORY_OPTION_GROUPS: ProductOptionGroup[] = [
  {
    key: 'size',
    label: buildLocalizedShopText('Size', '尺码'),
    type: 'button',
    options: [{ value: 'One Size', label: buildLocalizedShopText('One Size', '均码') }],
    pill: true,
    required: true,
    defaultValue: 'One Size',
  },
];
